import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Car, ChevronDown, Search, Plus, Trash2, Pencil, Check, Loader2,
  MapPin, Upload, FileText, AlertTriangle, ReceiptText, ExternalLink,
  CheckCircle2, Circle, Lock, ShieldCheck,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { loanProfilingApi } from '@/services/api';
import INDIAN_CITIES from '@/utils/indianCities';
import { SectionHeading } from './CockpitUI';

const reveal = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25 } };
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Everything the registry returns beyond the headline fields — lenders ask for
// chassis, engine, fitness and tax too, so the whole record is carried along.
const rcExtras = (d = {}) => ({
  fuel_type: d.fuel_type || '',
  color: d.color || '',
  chassis_number: d.chassis_number || '',
  engine_number: d.engine_number || '',
  vehicle_class: d.vehicle_class || '',
  body_type: d.body_type || '',
  seating_capacity: d.seating_capacity ? String(d.seating_capacity) : '',
  cubic_capacity: d.cubic_capacity ? String(d.cubic_capacity) : '',
  rto_code: d.rto_code || '',
  registration_authority: d.registration_authority || '',
  rc_status: d.rc_status || d.status || '',
  fitness_upto: d.fitness_upto || '',
  tax_upto: d.tax_valid_upto || d.tax_paid_upto || '',
  pucc_upto: d.pucc_valid_upto || '',
  financer: d.financer || d.hypothecation_bank || '',
  permanent_address: d.permanent_address || '',
  present_address: d.present_address || '',
  vaahan: Object.fromEntries(Object.entries(d).filter(([k, v]) =>
    !['raw_data', 'response_metadata'].includes(k) && v !== null && v !== '' && typeof v !== 'object')),
});

// ───────────────────────── source badge ─────────────────────────
const SourceBadge = ({ source }) => {
  if (source === 'inspection') return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-200">Inspection</span>;
  if (source === 'customer') return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-blue-50 text-blue-600 border border-blue-200">Customer master</span>;
  if (source === 'report') return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-blue-50 text-blue-600 border border-blue-200">Report</span>;
  if (source === 'surepass') return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-200">RC Verified</span>;
  return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-amber-50 text-amber-600 border border-amber-200">Manual</span>;
};

// ───────────────────────── city combobox ─────────────────────────
const CityCombobox = ({ value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="flex h-10 w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 hover:bg-slate-50 transition-colors">
          <span className={`truncate ${value ? 'text-slate-900' : 'text-slate-400'}`}>{value || 'Select city, state…'}</span>
          <MapPin className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search city or state…" data-testid="location-combobox-search" />
          <CommandList>
            <CommandEmpty>No city found.</CommandEmpty>
            <CommandGroup>
              {INDIAN_CITIES.map((c) => (
                <CommandItem key={c} value={c} onSelect={() => { onChange(c); setOpen(false); }}
                  data-testid={`location-option-${c}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === c ? 'opacity-100 text-blue-600' : 'opacity-0'}`} />
                  {c}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// ───────────────────────── field wrapper ─────────────────────────
const Field = ({ label, source, children }) => (
  <div className="flex flex-col space-y-1.5">
    <div className="flex items-center justify-between gap-2">
      <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">{label}</Label>
      {source && <SourceBadge source={source} />}
    </div>
    {children}
  </div>
);

const YesNo = ({ value, onChange, testId }) => (
  <div className="inline-flex rounded-md border border-slate-300 overflow-hidden h-10" data-testid={testId}>
    {[{ k: true, l: 'Yes' }, { k: false, l: 'No' }].map((o) => (
      <button key={o.l} type="button" onClick={() => onChange(o.k)}
        data-testid={`${testId}-${o.l.toLowerCase()}`}
        className={`px-5 text-sm font-medium transition-colors ${value === o.k ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
        {o.l}
      </button>
    ))}
  </div>
);

// ───────────────────────── challans panel ─────────────────────────
const ChallansPanel = ({ challans, loading, error, onCheck }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="challans-panel">
    <div className="flex items-center justify-between gap-3 mb-1">
      <div className="flex items-center gap-2">
        <ReceiptText className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-semibold text-slate-800">Traffic Challans</span>
        {challans && (
          <span className="text-[11px] text-slate-500">· {challans.total_challans} total · {challans.pending_count} pending</span>
        )}
      </div>
      <Button size="sm" variant="outline" onClick={onCheck} disabled={loading} data-testid="check-challans-button">
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
        {challans ? 'Refresh' : 'Check challans'}
      </Button>
    </div>
    <p className="text-xs text-slate-400 mb-3">Via Surepass · cached up to 30 days to avoid repeat charges.</p>

    {error && (
      <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="challans-error">
        <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
      </div>
    )}

    {challans && (
      <div data-testid="challans-result">
        {challans.pending_count > 0 && (
          <div className="flex items-center gap-2 text-sm font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-3">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {challans.pending_count} pending challan(s) · {inr(challans.total_pending_amount)} outstanding
          </div>
        )}
        {(challans.challan_details || []).length === 0 ? (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="challans-none">
            No challans found for this vehicle. 🎉
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {challans.challan_details.map((c, i) => {
              const pending = (c.challan_status || '').toLowerCase() === 'pending';
              return (
                <div key={c.challan_number || i} className="flex items-start justify-between gap-3 bg-white border border-slate-200 rounded-lg p-3" data-testid={`challan-row-${i}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.offense_details || 'Traffic offense'}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      #{c.challan_number || '—'} · {c.challan_date || '—'}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-slate-900">{inr(c.amount)}</div>
                    <span className={`inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${pending ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {c.challan_status || '—'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    )}
  </div>
);

// ───────────────────────── vehicle form ─────────────────────────
const inputCls = 'flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 transition-all';

const VehicleForm = ({ initial, leadId, onSave, onCancel, saving }) => {
  const [f, setF] = useState(() => ({ ...initial }));
  const [sources, setSources] = useState(() => ({ ...(initial.field_sources || {}) }));
  const [formSource, setFormSource] = useState(initial.source || 'manual');
  const [uploading, setUploading] = useState(false);
  const [challans, setChallans] = useState(null);
  const [challanLoading, setChallanLoading] = useState(false);
  const [challanError, setChallanError] = useState(null);
  // Reg number is locked once data has been fetched (RC/inspection) to keep the
  // dataset consistent. Editing it triggers a fresh internal-DB → Surepass lookup.
  const [regEditing, setRegEditing] = useState(() => !(initial.source === 'surepass' || initial.source === 'inspection'));
  const [reFetching, setReFetching] = useState(false);
  const fileRef = useRef(null);

  const srcOf = (k) => sources[k] || (f[k] ? formSource : 'manual');
  const upd = (k, v) => { setF((p) => ({ ...p, [k]: v })); setSources((p) => ({ ...p, [k]: 'manual' })); };

  const doReFetch = async () => {
    const reg = String(f.registration_number || '').replace(/\s/g, '').toUpperCase();
    if (!reg) { toast.error('Enter a registration number'); return; }
    setReFetching(true);
    try {
      const res = await loanProfilingApi.fetchRc(reg);
      const d = res.data?.data || {};
      const yr = (() => {
        const m = d.manufacturing_date || '';
        if (m.includes('/')) return m.split('/').pop();
        return m ? String(m).slice(0, 4) : '';
      })();
      const fetched = {
        make: d.manufacturer || '',
        model: d.model || '',
        manufacture_year: yr && /^\d{4}$/.test(yr) ? parseInt(yr, 10) : '',
        rc_owner_name: d.owner_name || '',
        ownership: d.owner_count ? String(d.owner_count) : '',
        registration_date: d.registration_date || '',
        insurer_name: d.insurance_company || '',
        insurance_expiry: d.insurance_valid_upto || '',
        hypothecation: d.hypothecation_bank || d.financer || '',
        hpa_name: d.hypothecation_bank || d.financer || '',
        ...rcExtras(d),
      };
      // Replace RC-driven fields wholesale; preserve rep-entered manual fields.
      setF((prev) => ({
        ...prev,
        registration_number: reg,
        ...fetched,
        variant: prev.variant || '',
      }));
      const fs = {};
      Object.keys(fetched).forEach((k) => { if (fetched[k]) fs[k] = 'surepass'; });
      setSources((prev) => ({ ...prev, ...fs }));
      setFormSource('surepass');
      setRegEditing(false);
      setChallans(null);
      setChallanError(null);
      toast.success(`RC details refreshed (${res.data?.source === 'local_db' ? 'internal DB' : 'Surepass'})`);
    } catch (e) {
      toast.message('RC fetch unavailable — you can enter the details manually.');
      setRegEditing(false);
    } finally {
      setReFetching(false);
    }
  };

  const onRcUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await loanProfilingApi.uploadDocument(leadId, 'vehicle_rc', file);
      const doc = res.data.document;
      setF((p) => ({ ...p, rc_copy_doc_id: doc.id, rc_copy_file_name: doc.file_name }));
      toast.success('RC copy uploaded');
    } catch (err) {
      toast.error('RC copy upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const checkChallans = async () => {
    const reg = String(f.registration_number || '').replace(/\s/g, '').toUpperCase();
    if (!reg) { toast.error('Enter a registration number first'); return; }
    setChallanLoading(true);
    setChallanError(null);
    try {
      const res = await loanProfilingApi.vehicleChallans(reg);
      if (res.data?.success) {
        setChallans(res.data.data);
      } else {
        setChallans(null);
        setChallanError(res.data?.error || 'Could not fetch challans');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Challan service unavailable right now.';
      setChallanError(typeof msg === 'string' ? msg : 'Challan fetch failed');
    } finally {
      setChallanLoading(false);
    }
  };

  const submit = () => {
    if (!f.registration_number || !String(f.registration_number).trim()) {
      toast.error('Registration number is required');
      return;
    }
    onSave({
      registration_number: String(f.registration_number || '').toUpperCase().trim(),
      make: f.make || '',
      model: f.model || '',
      variant: f.variant || '',
      manufacture_year: f.manufacture_year ? parseInt(f.manufacture_year, 10) : null,
      location: f.location || '',
      ownership: f.ownership || '',
      rc_owner_name: f.rc_owner_name || '',
      odometer: (f.odometer !== '' && f.odometer != null) ? parseInt(f.odometer, 10) : null,
      hypothecation: f.hypothecation || '',
      purchase_cost: (f.purchase_cost !== '' && f.purchase_cost != null) ? parseFloat(f.purchase_cost) : null,
      rc_copy_available: typeof f.rc_copy_available === 'boolean' ? f.rc_copy_available : null,
      rc_copy_doc_id: f.rc_copy_doc_id || null,
      rc_copy_file_name: f.rc_copy_file_name || null,
      registration_date: f.registration_date || '',
      insurer_name: f.insurer_name || '',
      insurance_expiry: f.insurance_expiry || '',
      hpa_name: f.hpa_name || '',
      inspection_id: f.inspection_id || null,
      source: formSource || 'manual',
      field_sources: sources,
      vehicle_valuation: (f.vehicle_valuation !== '' && f.vehicle_valuation != null) ? parseFloat(f.vehicle_valuation) : null,
      valuation_min: f.valuation_min ?? null,
      valuation_max: f.valuation_max ?? null,
      valuation_source: sources.vehicle_valuation === 'manual' && f.vehicle_valuation ? 'manual' : (f.valuation_source || null),
      fuel_type: f.fuel_type || '',
      color: f.color || '',
      chassis_number: f.chassis_number || '',
      engine_number: f.engine_number || '',
      vehicle_class: f.vehicle_class || '',
      body_type: f.body_type || '',
      seating_capacity: f.seating_capacity || '',
      cubic_capacity: f.cubic_capacity || '',
      rto_code: f.rto_code || '',
      registration_authority: f.registration_authority || '',
      rc_status: f.rc_status || '',
      fitness_upto: f.fitness_upto || '',
      tax_upto: f.tax_upto || '',
      pucc_upto: f.pucc_upto || '',
      financer: f.financer || '',
      permanent_address: f.permanent_address || '',
      present_address: f.present_address || '',
      vaahan: f.vaahan || null,
    });
  };

  return (
    <motion.div {...reveal} className="flex flex-col gap-6 p-5 border border-slate-200 rounded-xl bg-white shadow-sm" data-testid="vehicle-details-panel">
      {/* RC / vehicle data */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field label="Reg No" source={!regEditing && f.registration_number ? srcOf('registration_number') : null}>
          {regEditing ? (
            <div className="flex items-center gap-2">
              <Input value={f.registration_number || ''} onChange={(e) => upd('registration_number', e.target.value.toUpperCase())}
                className={`${inputCls} uppercase`} placeholder="e.g. KA01AB1234" data-testid="field-editable-registration_number"
                onKeyDown={(e) => { if (e.key === 'Enter') doReFetch(); }} />
              <Button type="button" size="sm" onClick={doReFetch} disabled={reFetching} className="bg-blue-600 hover:bg-blue-700 h-10 shrink-0 px-3" data-testid="reg-refetch-button">
                {reFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex h-10 flex-1 items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-800" data-testid="field-readonly-registration_number">
                <span className="truncate">{f.registration_number || '—'}</span>
                <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => setRegEditing(true)} className="h-10 shrink-0" data-testid="edit-reg-button">
                <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit
              </Button>
            </div>
          )}
        </Field>
        <Field label="Year of Mfg" source={srcOf('manufacture_year')}>
          <Input type="number" value={f.manufacture_year || ''} onChange={(e) => upd('manufacture_year', e.target.value)}
            placeholder="e.g. 2019" className={inputCls} data-testid="field-editable-manufacture_year" />
        </Field>
        <Field label="Make" source={srcOf('make')}>
          <Input value={f.make || ''} onChange={(e) => upd('make', e.target.value)} className={inputCls} data-testid="field-editable-make" />
        </Field>
        <Field label="Model" source={srcOf('model')}>
          <Input value={f.model || ''} onChange={(e) => upd('model', e.target.value)} className={inputCls} data-testid="field-editable-model" />
        </Field>
        <Field label="Variant" source={srcOf('variant')}>
          <Input value={f.variant || ''} onChange={(e) => upd('variant', e.target.value)} placeholder="e.g. ZXi AMT" className={inputCls} data-testid="field-editable-variant" />
        </Field>
        <Field label="Vehicle Location" source={srcOf('location')}>
          <CityCombobox value={f.location} onChange={(v) => upd('location', v)} testId="location-combobox" />
        </Field>
        <Field label="Ownership" source={srcOf('ownership')}>
          <Input value={f.ownership || ''} onChange={(e) => upd('ownership', e.target.value)} placeholder="e.g. 1st owner" className={inputCls} data-testid="field-editable-ownership" />
        </Field>
        <Field label="RC Owner Name" source={srcOf('rc_owner_name')}>
          <Input value={f.rc_owner_name || ''} onChange={(e) => upd('rc_owner_name', e.target.value)} className={inputCls} data-testid="field-editable-rc_owner_name" />
        </Field>
        <Field label="Odometer (km)" source={srcOf('odometer')}>
          <Input type="number" value={f.odometer ?? ''} onChange={(e) => upd('odometer', e.target.value)} className={inputCls} data-testid="field-editable-odometer" />
        </Field>
        <Field label="Existing Hypothecation" source={srcOf('hypothecation')}>
          <Input value={f.hypothecation || ''} onChange={(e) => upd('hypothecation', e.target.value)} placeholder="e.g. HDFC Bank / None" className={inputCls} data-testid="field-editable-hypothecation" />
        </Field>
        <Field label="Reg Date" source={srcOf('registration_date')}>
          <Input value={f.registration_date || ''} onChange={(e) => upd('registration_date', e.target.value)} placeholder="DD/MM/YYYY" className={inputCls} data-testid="field-editable-registration_date" />
        </Field>
        <Field label="Insurer Name" source={srcOf('insurer_name')}>
          <Input value={f.insurer_name || ''} onChange={(e) => upd('insurer_name', e.target.value)} className={inputCls} data-testid="field-editable-insurer_name" />
        </Field>
        <Field label="Insurance Exp Date" source={srcOf('insurance_expiry')}>
          <Input value={f.insurance_expiry || ''} onChange={(e) => upd('insurance_expiry', e.target.value)} placeholder="DD/MM/YYYY" className={inputCls} data-testid="field-editable-insurance_expiry" />
        </Field>
        <Field label="HPA Name" source={srcOf('hpa_name')}>
          <Input value={f.hpa_name || ''} onChange={(e) => upd('hpa_name', e.target.value)} placeholder="Financier on RC" className={inputCls} data-testid="field-editable-hpa_name" />
        </Field>
      </div>

      {/* RC copy + Purchase cost */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col space-y-1.5">
          <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">RC Copy Available</Label>
          <div className="flex items-center gap-3 flex-wrap">
            <YesNo value={f.rc_copy_available} onChange={(v) => upd('rc_copy_available', v)} testId="field-editable-rc_copy" />
            {f.rc_copy_available === true && (
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onRcUpload} data-testid="rc-copy-file-input" />
                <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="rc-copy-upload-button">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                  {f.rc_copy_doc_id ? 'Replace' : 'Upload RC copy'}
                </Button>
                {f.rc_copy_doc_id && (
                  <a href={loanProfilingApi.fileUrl(leadId, f.rc_copy_doc_id)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline max-w-[160px] truncate" data-testid="rc-copy-view-link">
                    <FileText className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{f.rc_copy_file_name || 'View'}</span>
                    <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        <Field label="Purchase Cost (₹)" source={srcOf('purchase_cost')}>
          <Input type="number" value={f.purchase_cost ?? ''} onChange={(e) => upd('purchase_cost', e.target.value)} className={inputCls} data-testid="field-editable-purchase_cost" />
        </Field>
        <Field label="Car Valuation (₹)" source={srcOf('vehicle_valuation')}>
          <Input type="number" value={f.vehicle_valuation ?? ''} onChange={(e) => upd('vehicle_valuation', e.target.value)}
            placeholder="Average of the report's recommended price" className={inputCls} data-testid="field-editable-vehicle_valuation" />
          {(f.valuation_min || f.valuation_max) && (
            <span className="text-[11px] text-slate-500 mt-1" data-testid="valuation-band">
              Inspection report recommends {inr(f.valuation_min)} – {inr(f.valuation_max)}
            </span>
          )}
        </Field>
      </div>

      <VaahanRecord vaahan={f.vaahan} />

      <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
        <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="vehicle-form-cancel">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-vehicle-button">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          {saving ? 'Saving…' : 'Save vehicle'}
        </Button>
      </div>

      {/* Challans — at the bottom, after save */}
      <ChallansPanel challans={challans} loading={challanLoading} error={challanError} onCheck={checkChallans} />
    </motion.div>
  );
};

// ───────────────────────── inspected car card ─────────────────────────
const InspectedCarCard = ({ car, onUse, busy }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-xl bg-white overflow-hidden mb-3 transition-all ${
      car.is_loan_car ? 'border-blue-400 ring-1 ring-blue-300' : 'border-slate-200'
    }`} data-testid={`inspected-car-card-${car.registration_number}`}>
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50" onClick={() => setOpen((o) => !o)}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><Car className="w-4 h-4" /></span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{car.registration_number}</div>
            <div className="text-xs text-slate-500 flex items-center gap-2 truncate">
              {[car.make, car.model, car.manufacture_year].filter(Boolean).join(' ') || 'Details on file'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {car.is_loan_car && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700"
              data-testid={`loan-car-flag-${car.registration_number}`}>
              Loan car
            </span>
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800">Inspected</span>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-100 bg-slate-50 overflow-hidden">
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <Detail label="Owner" value={car.rc_owner_name} />
              <Detail label="Ownership" value={car.ownership} />
              <Detail label="Odometer" value={car.odometer ? `${Number(car.odometer).toLocaleString('en-IN')} km` : null} />
              <Detail label="Car valuation" value={car.vehicle_valuation ? inr(car.vehicle_valuation) : null} />
              <Detail label="Location" value={car.location} />
              <Detail label="Insurer" value={car.insurer_name} />
              <Detail label="Insurance exp" value={car.insurance_expiry} />
              <Detail label="Hypothecation" value={car.hypothecation} />
            </div>
            <div className="px-4 pb-4">
              <Button size="sm" onClick={() => onUse(car)} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid={`use-inspected-car-${car.registration_number}`}>
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                {busy ? 'Fetching details…' : 'Use this vehicle'}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Detail = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{label}</div>
    <div className="text-slate-700 font-medium">{value || '—'}</div>
  </div>
);

// ───────────────────────── full registry record ─────────────────────────
const VAAHAN_LABELS = {
  registration_number: 'Reg number', registration_date: 'Reg date', manufacturer_full: 'Manufacturer',
  model: 'Model', manufacturing_date: 'Mfg date', fuel_type: 'Fuel', color: 'Colour',
  chassis_number: 'Chassis', engine_number: 'Engine', vehicle_class: 'Vehicle class',
  body_type: 'Body type', category: 'Category', cubic_capacity: 'Engine CC',
  cylinders: 'Cylinders', seating_capacity: 'Seats', gross_weight: 'Gross weight',
  unladen_weight: 'Unladen weight', wheelbase: 'Wheelbase', emission_norms: 'Emission norms',
  owner_count: 'Owners', owner_name: 'RC owner', father_name: "Owner's father",
  permanent_address: 'Permanent address', present_address: 'Present address',
  rto_code: 'RTO', registration_authority: 'Registering authority', rc_status: 'RC status',
  rc_expiry_date: 'RC expiry', fitness_upto: 'Fitness upto', tax_valid_upto: 'Tax valid upto',
  tax_paid_upto: 'Tax paid upto', pucc_number: 'PUCC number', pucc_valid_upto: 'PUCC valid upto',
  insurance_company: 'Insurer', insurance_policy_number: 'Policy number',
  insurance_valid_upto: 'Insurance valid upto', financed: 'Financed', financer: 'Financer',
  noc_details: 'NOC', blacklist_status: 'Blacklist', is_commercial: 'Commercial',
  permit_number: 'Permit number', permit_type: 'Permit type', permit_valid_upto: 'Permit upto',
  national_permit_number: 'National permit', national_permit_upto: 'National permit upto',
  mobile_number: 'Mobile on RC', status_as_on: 'Registry data as on', api_provider: 'Source',
};
const VAAHAN_ORDER = Object.keys(VAAHAN_LABELS);

export const VaahanRecord = ({ vaahan, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!vaahan || Object.keys(vaahan).length === 0) return null;
  const keys = [...VAAHAN_ORDER.filter((k) => vaahan[k] != null && vaahan[k] !== ''),
    ...Object.keys(vaahan).filter((k) => !VAAHAN_LABELS[k] && typeof vaahan[k] !== 'object' && vaahan[k] !== '')];
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="vaahan-record">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid="vaahan-record-toggle">
        <span className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
          Full Vaahan record
          <span className="text-[10px] font-medium text-slate-400">{keys.length} fields</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-slate-100">
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs bg-white">
              {keys.map((k) => (
                <Detail key={k} testId={`vaahan-field-${k}`} label={VAAHAN_LABELS[k] || k.replace(/_/g, ' ')}
                  value={typeof vaahan[k] === 'boolean' ? (vaahan[k] ? 'Yes' : 'No') : String(vaahan[k])} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ───────────────────────── saved vehicle card ─────────────────────────
const SavedVehicleCard = ({ vehicle, leadId, onEdit, onRemove, onFinalize, removing, finalizing }) => {
  const [open, setOpen] = useState(false);
  const finalized = vehicle.is_finalized === true;
  return (
    <div className={`border rounded-xl bg-white overflow-hidden mb-3 transition-all ${finalized ? 'ring-2 ring-blue-500 border-blue-300' : 'border-slate-200'}`} data-testid={`saved-vehicle-card-${vehicle.id}`}>
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button type="button" onClick={() => onFinalize(vehicle)} disabled={finalizing || finalized}
            title={finalized ? 'Finalized for loan' : 'Select this car for the loan'}
            className="shrink-0" data-testid={`finalize-vehicle-${vehicle.id}`}>
            {finalizing ? <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              : finalized ? <CheckCircle2 className="w-5 h-5 text-blue-600" />
              : <Circle className="w-5 h-5 text-slate-300 hover:text-blue-400" />}
          </button>
          <div className="flex items-center gap-3 min-w-0 cursor-pointer flex-1" onClick={() => setOpen((o) => !o)}
            data-testid={`expand-vehicle-${vehicle.id}`}>
            <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Car className="w-4 h-4" /></span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                {vehicle.registration_number || 'Vehicle'}
                {finalized && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-blue-50 text-blue-600 border border-blue-200" data-testid={`finalized-badge-${vehicle.id}`}>Finalized for loan</span>}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {[vehicle.make, vehicle.model, vehicle.variant, vehicle.manufacture_year].filter(Boolean).join(' ') || '—'}
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onEdit(vehicle)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`edit-vehicle-${vehicle.id}`}><Pencil className="w-4 h-4" /></button>
          <button onClick={() => onRemove(vehicle)} disabled={removing} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" data-testid={`remove-vehicle-${vehicle.id}`}>
            {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-100 bg-slate-50 overflow-hidden">
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <Detail label="Location" value={vehicle.location} />
              <Detail label="Ownership" value={vehicle.ownership} />
              <Detail label="RC owner" value={vehicle.rc_owner_name} />
              <Detail label="Odometer" value={vehicle.odometer ? `${Number(vehicle.odometer).toLocaleString('en-IN')} km` : null} />
              <Detail label="Car valuation" value={vehicle.vehicle_valuation ? inr(vehicle.vehicle_valuation) : null} />
              <Detail label="Hypothecation" value={vehicle.hypothecation} />
              <Detail label="Purchase cost" value={vehicle.purchase_cost ? inr(vehicle.purchase_cost) : null} />
              <Detail label="Reg date" value={vehicle.registration_date} />
              <Detail label="Insurer" value={vehicle.insurer_name} />
              <Detail label="Insurance exp" value={vehicle.insurance_expiry} />
              <Detail label="HPA name" value={vehicle.hpa_name} />
              <Detail label="RC copy" value={typeof vehicle.rc_copy_available === 'boolean' ? (vehicle.rc_copy_available ? 'Yes' : 'No') : null} />
              <div>
                <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">RC document</div>
                {vehicle.rc_copy_doc_id ? (
                  <a href={loanProfilingApi.fileUrl(leadId, vehicle.rc_copy_doc_id)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline font-medium" data-testid={`saved-rc-doc-${vehicle.id}`}>
                    <FileText className="w-3.5 h-3.5" /> View <ExternalLink className="w-3 h-3" />
                  </a>
                ) : <div className="text-slate-700 font-medium">—</div>}
              </div>
            </div>
            <div className="px-4 pb-4">
              <VaahanRecord vaahan={vehicle.vaahan} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ───────────────────────── main block ─────────────────────────
export const VehicleCaptureBlock = ({ leadId, vehicles = [], onChanged }) => {
  const [inspected, setInspected] = useState([]);
  const [loadingInspected, setLoadingInspected] = useState(true);
  const [regInput, setRegInput] = useState('');
  const [fetching, setFetching] = useState(false);
  const [formInitial, setFormInitial] = useState(null); // open the form when set
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [finalizingId, setFinalizingId] = useState(null);
  const [enrichingReg, setEnrichingReg] = useState(null);

  const loadInspected = useCallback(async () => {
    try {
      const res = await loanProfilingApi.inspectedVehicles(leadId);
      setInspected(res.data.vehicles || []);
    } catch (e) {
      setInspected([]);
    } finally {
      setLoadingInspected(false);
    }
  }, [leadId]);

  useEffect(() => { loadInspected(); }, [loadInspected]);

  const openFromInspection = async (car) => {
    setEditingId(null);
    const reg = String(car.registration_number || '').replace(/\s/g, '').toUpperCase();
    const baseSources = Object.keys(car).reduce((acc, k) => {
      if (['variant', 'purchase_cost'].includes(k)) acc[k] = 'manual';
      else if (k === 'location') acc[k] = 'customer';
      else if (['vehicle_valuation', 'valuation_min', 'valuation_max'].includes(k)) acc[k] = 'report';
      else if (car[k]) acc[k] = 'inspection';
      return acc;
    }, {});
    let init = { ...car, source: 'inspection', field_sources: baseSources };

    // Auto-enrich from Surepass only when key RC fields are missing (the lookup
    // reuses the 30-day server cache). Inspection-sourced values are preserved;
    // if the fetch fails the rep can still complete the blanks manually.
    const needsEnrich = reg && !(car.make && car.rc_owner_name && car.insurer_name && car.ownership);
    if (needsEnrich) {
      setEnrichingReg(car.registration_number);
      try {
        const res = await loanProfilingApi.fetchRc(reg);
        const d = res.data?.data || {};
        const yr = (() => {
          const m = d.manufacturing_date || '';
          if (m.includes('/')) return m.split('/').pop();
          return m ? String(m).slice(0, 4) : '';
        })();
        const fetched = {
          make: d.manufacturer || '',
          model: d.model || '',
          manufacture_year: yr && /^\d{4}$/.test(yr) ? parseInt(yr, 10) : '',
          rc_owner_name: d.owner_name || '',
          ownership: d.owner_count ? String(d.owner_count) : '',
          registration_date: d.registration_date || '',
          insurer_name: d.insurance_company || '',
          insurance_expiry: d.insurance_valid_upto || '',
          hypothecation: d.hypothecation_bank || d.financer || '',
          hpa_name: d.hypothecation_bank || d.financer || '',
          ...rcExtras(d),
        };
        const merged = { ...init };
        const fs = { ...baseSources };
        Object.entries(fetched).forEach(([k, v]) => {
          if (v && !merged[k]) { merged[k] = v; fs[k] = 'surepass'; }
        });
        init = { ...merged, registration_number: reg, field_sources: fs };
        toast.success(`Filled missing details from ${res.data?.source === 'local_db' ? 'cached RC' : 'Surepass'}`);
      } catch (e) {
        toast.message('RC fetch unavailable — please complete the missing details manually.');
      } finally {
        setEnrichingReg(null);
      }
    }
    setFormInitial(init);
  };

  const fetchRc = async () => {
    const reg = regInput.replace(/\s/g, '').toUpperCase();
    if (!reg) { toast.error('Enter a registration number'); return; }
    setFetching(true);
    try {
      const res = await loanProfilingApi.fetchRc(reg);
      const d = res.data?.data || {};
      const yr = (() => {
        const m = d.manufacturing_date || '';
        if (m.includes('/')) return m.split('/').pop();
        return m ? String(m).slice(0, 4) : '';
      })();
      const mapped = {
        registration_number: reg,
        make: d.manufacturer || '',
        model: d.model || '',
        variant: '',
        manufacture_year: yr && /^\d{4}$/.test(yr) ? parseInt(yr, 10) : '',
        rc_owner_name: d.owner_name || '',
        ownership: d.owner_count ? String(d.owner_count) : '',
        registration_date: d.registration_date || '',
        insurer_name: d.insurance_company || '',
        insurance_expiry: d.insurance_valid_upto || '',
        hypothecation: d.hypothecation_bank || d.financer || '',
        hpa_name: d.hypothecation_bank || d.financer || '',
        ...rcExtras(d),
        source: 'surepass',
      };
      const fs = {};
      ['make', 'model', 'manufacture_year', 'rc_owner_name', 'ownership', 'registration_date', 'insurer_name', 'insurance_expiry', 'hypothecation', 'hpa_name'].forEach((k) => { if (mapped[k]) fs[k] = 'surepass'; });
      setEditingId(null);
      setFormInitial({ ...mapped, field_sources: fs });
      toast.success(`RC details fetched (${res.data?.source === 'local_db' ? 'cached' : 'live'})`);
    } catch (e) {
      toast.message('RC fetch unavailable — please enter the details manually.');
      setEditingId(null);
      setFormInitial({ registration_number: reg, source: 'manual', field_sources: {} });
    } finally {
      setFetching(false);
    }
  };

  const startBlank = () => {
    setEditingId(null);
    setFormInitial({ source: 'manual', field_sources: {} });
  };

  const editVehicle = (v) => {
    setEditingId(v.id);
    setFormInitial({ ...v });
  };

  const saveVehicle = async (payload) => {
    setSaving(true);
    try {
      if (editingId) {
        await loanProfilingApi.updateVehicle(leadId, editingId, payload);
        toast.success('Vehicle updated');
      } else {
        await loanProfilingApi.addVehicle(leadId, payload);
        toast.success('Vehicle saved');
      }
      setFormInitial(null);
      setEditingId(null);
      setRegInput('');
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error('Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const removeVehicle = async (v) => {
    setRemovingId(v.id);
    try {
      await loanProfilingApi.removeVehicle(leadId, v.id);
      toast.success('Vehicle removed');
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error('Failed to remove vehicle');
    } finally {
      setRemovingId(null);
    }
  };

  const finalizeVehicle = async (v) => {
    setFinalizingId(v.id);
    try {
      await loanProfilingApi.finalizeVehicle(leadId, v.id);
      toast.success('Selected as the car for this loan');
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error('Failed to select vehicle');
    } finally {
      setFinalizingId(null);
    }
  };

  return (
    <div className="mt-10 pt-8 border-t border-slate-200" data-testid="vehicle-capture-block">
      <SectionHeading hint="Pick from the customer's inspected cars or fetch a new RC. All fields stay editable.">Vehicle Details</SectionHeading>

      {/* Saved vehicles */}
      {vehicles.length > 0 && (
        <div className="mb-6" data-testid="saved-vehicles-list">
          {vehicles.length > 1 && (
            <p className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-blue-600" /> Select the car this loan is requested for.
            </p>
          )}
          {vehicles.map((v) => (
            <SavedVehicleCard key={v.id} vehicle={v} leadId={leadId} onEdit={editVehicle} onRemove={removeVehicle}
              onFinalize={finalizeVehicle} removing={removingId === v.id} finalizing={finalizingId === v.id} />
          ))}
        </div>
      )}

      {/* Editor */}
      {formInitial ? (
        <VehicleForm key={editingId || formInitial.registration_number || 'new'} initial={formInitial} leadId={leadId} saving={saving} onSave={saveVehicle} onCancel={() => { setFormInitial(null); setEditingId(null); }} />
      ) : (
        <Tabs defaultValue="inspected" className="w-full">
          <TabsList className="flex space-x-1 rounded-lg bg-slate-100 p-1 mb-5">
            <TabsTrigger value="inspected" className="flex-1" data-testid="tab-inspected-cars">From inspections</TabsTrigger>
            <TabsTrigger value="manual" className="flex-1" data-testid="tab-manual-rc">Enter Reg Number</TabsTrigger>
          </TabsList>

          <TabsContent value="inspected">
            {loadingInspected ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 p-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading inspected cars…</div>
            ) : inspected.length === 0 ? (
              <div className="text-center p-6 border border-dashed border-slate-200 rounded-xl bg-slate-50" data-testid="no-inspected-cars">
                <Car className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No inspected cars found for this customer.</p>
                <Button variant="outline" className="mt-3" onClick={startBlank} data-testid="add-vehicle-manually-btn"><Plus className="w-4 h-4 mr-1" /> Add vehicle manually</Button>
              </div>
            ) : (
              <div>
                {inspected.map((car) => <InspectedCarCard key={car.registration_number} car={car} onUse={openFromInspection} busy={enrichingReg === car.registration_number} />)}
                <Button variant="outline" className="mt-1" onClick={startBlank} data-testid="add-vehicle-manually-btn"><Plus className="w-4 h-4 mr-1" /> Add a different vehicle</Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="manual">
            <div className="flex items-end gap-3 p-4 border border-slate-200 rounded-xl bg-slate-50">
              <div className="flex-1 space-y-1.5">
                <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Registration Number</Label>
                <Input value={regInput} onChange={(e) => setRegInput(e.target.value.toUpperCase())}
                  placeholder="e.g. KA01AB1234" className={`${inputCls} uppercase`} data-testid="rc-fetch-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchRc(); }} />
              </div>
              <Button onClick={fetchRc} disabled={fetching} className="bg-blue-600 hover:bg-blue-700 h-10 px-6" data-testid="rc-fetch-button">
                {fetching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                {fetching ? 'Fetching…' : 'Fetch'}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2">Fetches RC details via Surepass. You can edit anything before saving.</p>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default VehicleCaptureBlock;
